/**
 * Wiki-link support for the Tiptap markdown editor.
 *
 * Strategy: rather than introducing a new node/mark type (which would require
 * a custom markdown tokenizer AND serializer to roundtrip cleanly), we treat
 * wiki-links as ordinary markdown links —
 * just with a hrefs the consumer recognises (e.g. hearth uses
 * `/files/<path>`).
 *
 * - Storage form (in Automerge):  `[[Justin]]` or `[[Justin|display text]]`
 * - In-editor form (markdown):    `[display text](<wikiHref(Justin)>)`
 * - DOM:                          `<a href="<wikiHref(Justin)>">…</a>` with
 *                                 `data-wiki-link` / `data-wiki-missing`
 *                                 attributes painted on by a ProseMirror
 *                                 decoration so CSS can style them.
 *
 * The URL scheme is supplied by the consumer (`toHref` / `parseHref`)
 * so this package stays agnostic of any host app's routing convention. The
 * transforms are pure string functions so they're trivial to test, and the
 * input rule converts user-typed `[[Title]]` to the in-editor link form
 * immediately rather than waiting for an Automerge roundtrip.
 */
import { Extension, InputRule } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export interface WikiUrlAdapter {
  /** Build the href written into the in-editor markdown link for a target. */
  toHref(target: string): string;
  /**
   * Extract a wiki target from an href. Return `null` when the href is not
   * a wiki link (so external/regular links pass through untouched).
   */
  parseHref(href: string | null | undefined): string | null;
}

/**
 * Storage `[[Title]]` → in-editor `[Title](<toHref(Title)>)`.
 *
 * Supports `[[Title|display]]` to render different anchor text. The wiki
 * target is passed verbatim to `toHref` — the adapter handles any
 * encoding required by its URL scheme.
 */
export function inflateWikiLinks(
  markdown: string,
  toHref: (target: string) => string,
): string {
  return markdown.replace(
    /\[\[([^\]\n|]+)(?:\|([^\]\n]+))?\]\]/g,
    (_, rawTarget: string, rawLabel?: string) => {
      const target = rawTarget.trim();
      if (!target) return _;
      const label = (rawLabel ?? target).trim();
      const escapedLabel = label.replace(/[\[\]]/g, "\\$&");
      return `[${escapedLabel}](${toHref(target)})`;
    },
  );
}

/**
 * In-editor `[Title](<href>)` → storage `[[Title]]`, but only for links
 * the adapter recognises as wiki-links (other links pass through).
 *
 * The roundtrip is lossy by design: links where label === target collapse
 * to the short form `[[Target]]`; differing labels expand to `[[Target|label]]`.
 */
export function deflateWikiLinks(
  markdown: string,
  parseHref: (href: string) => string | null,
): string {
  return markdown.replace(
    /\[([^\]\n]+)\]\(([^)\s]+)\)/g,
    (full, rawLabel: string, rawHref: string) => {
      const target = parseHref(rawHref);
      if (target === null) return full;
      const label = rawLabel.replace(/\\([\[\]])/g, "$1");
      return label === target ? `[[${target}]]` : `[[${target}|${label}]]`;
    },
  );
}

/**
 * Build the inline decorations that paint wiki-link state onto rendered
 * anchors. Walks the doc once per editor transaction, finds text nodes
 * carrying a link mark with a wiki href, and emits one inline
 * decoration per such node with `data-wiki-*` attributes. ProseMirror
 * merges these attributes onto the rendered `<a>` so CSS can style
 * resolved vs missing links — no React-side DOM scanning required.
 *
 * Returns null when the schema has no `link` mark (e.g. tests without
 * StarterKit) so the plugin can short-circuit.
 */
function buildWikiDecorations(
  doc: PMNode,
  paths: readonly string[] | null,
  parseHref: (href: string | null | undefined) => string | null,
): DecorationSet {
  const linkType = doc.type.schema.marks.link;
  if (!linkType) return DecorationSet.empty;
  const decorations: Decoration[] = [];
  doc.descendants((node, pos) => {
    if (!node.isText) return;
    const linkMark = node.marks.find((m) => m.type === linkType);
    if (!linkMark) return;
    const target = parseHref(linkMark.attrs.href as string | null);
    if (target === null) return;
    const attrs: Record<string, string> = {
      "data-wiki-link": "true",
      "data-wiki-target": target,
    };
    if (paths && resolveWikiTarget(target, paths) === null) {
      attrs["data-wiki-missing"] = "true";
    }
    decorations.push(Decoration.inline(pos, pos + node.nodeSize, attrs));
  });
  return DecorationSet.create(doc, decorations);
}

/**
 * Tiptap extension. Adds three ProseMirror pieces:
 *   1. An input rule converting typed `[[Title]]` to a link mark with
 *      `href = urls.toHref(Title)`. The cursor exits the link so the next
 *      keystroke isn't absorbed into it.
 *   2. A click plugin that intercepts left-clicks on wiki anchors and
 *      dispatches them to `storage.onClick`. Returning `true` stops PM
 *      from also placing the cursor before the consumer navigates away.
 *   3. A decoration plugin that paints `data-wiki-link`,
 *      `data-wiki-target`, and `data-wiki-missing` onto rendered anchors
 *      based on `storage.paths`. The plugin recomputes only when the
 *      `paths` reference in storage changes (signalled via a no-op
 *      transaction with the `wikiLinkFiles` meta), or when the document
 *      changes — keeping the per-keystroke work bounded.
 *
 * Both runtime knobs (`onClick`, `paths`) live on storage so the consuming
 * React component can swap them without recreating the editor.
 */
export interface WikiLinkOptions {
  /** URL adapter — see {@link WikiUrlAdapter}. */
  urls: WikiUrlAdapter;
  /** Initial click handler. Override at runtime via `storage.onClick`. */
  onClick?: (target: string) => void;
}

export interface WikiLinkStorage {
  onClick: ((target: string) => void) | null;
  /**
   * Every path in the project tree. The consumer mutates this directly and
   * then dispatches a transaction with the `wikiLinkFiles` meta to force
   * the decoration plugin to recompute (or the next user transaction will
   * pick it up naturally).
   */
  paths: readonly string[] | null;
}

declare module "@tiptap/core" {
  interface Storage {
    wikiLink: WikiLinkStorage;
  }
}

/**
 * Meta key consumers dispatch on a no-op transaction to force the wiki
 * decoration plugin to recompute (e.g. when the `paths` list changes
 * without any document edit).
 */
export const WIKI_LINK_FILES_META = "wikiLinkFiles";

export const WikiLink = Extension.create<WikiLinkOptions, WikiLinkStorage>({
  name: "wikiLink",

  addOptions() {
    // The consumer always supplies a real adapter via `configure(...)`;
    // the default here is a no-op that lets the extension stay valid in
    // contexts (e.g. unit tests) where no wiki support is wired up.
    return {
      urls: { toHref: (t) => t, parseHref: () => null },
      onClick: undefined,
    };
  },

  addStorage() {
    return { onClick: this.options.onClick ?? null, paths: null };
  },

  addInputRules() {
    const linkType = this.editor.schema.marks.link;
    if (!linkType) return [];
    const { toHref } = this.options.urls;

    return [
      new InputRule({
        find: /\[\[([^\]\n|]+?)(?:\|([^\]\n]+?))?\]\]$/,
        handler: ({ range, match, chain }) => {
          const target = match[1]?.trim();
          if (!target) return null;
          const label = (match[2] ?? target).trim();
          const href = toHref(target);

          chain()
            .deleteRange(range)
            .insertContent({
              type: "text",
              text: label,
              marks: [{ type: linkType.name, attrs: { href } }],
            })
            // Stop the link from absorbing the next keystroke.
            .unsetMark(linkType.name)
            .run();
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    const editor = this.editor;
    const { parseHref } = this.options.urls;
    const decorationsKey = new PluginKey<DecorationSet>("wikiLinkDecorations");
    return [
      new Plugin({
        key: new PluginKey("wikiLinkClick"),
        props: {
          handleClick(_view, _pos, event) {
            if (event.button !== 0) return false;
            const eventTarget = event.target;
            if (!(eventTarget instanceof Element)) return false;
            const anchor = eventTarget.closest("a[href]");
            if (!anchor) return false;
            const target = parseHref(anchor.getAttribute("href"));
            if (target === null) return false;
            event.preventDefault();
            const handler = editor.storage.wikiLink?.onClick;
            handler?.(target);
            // Returning true stops PM from doing its default click handling
            // (cursor placement) on top of our navigation.
            return true;
          },
        },
      }),
      new Plugin<DecorationSet>({
        key: decorationsKey,
        state: {
          init: (_, state) =>
            buildWikiDecorations(
              state.doc,
              editor.storage.wikiLink.paths,
              parseHref,
            ),
          apply: (tr, oldSet) => {
            const filesChanged = tr.getMeta(WIKI_LINK_FILES_META) === true;
            if (filesChanged || tr.docChanged) {
              return buildWikiDecorations(
                tr.doc,
                editor.storage.wikiLink.paths,
                parseHref,
              );
            }
            return oldSet.map(tr.mapping, tr.doc);
          },
        },
        props: {
          decorations(state) {
            return decorationsKey.getState(state) ?? null;
          },
        },
      }),
    ];
  },
});

/**
 * Resolve a wiki target to a real workspace path among `paths` (every
 * project-relative path in the tree, e.g. from `useTreeEntries`), or `null`
 * if there's nothing on disk to back it. The `.md` may be omitted, as the
 * agent and the `@`-mention autocomplete write `[[trips/italy/plan]]`.
 *
 * Tries the target verbatim, then with `.md`, then both case-insensitively.
 */
export function resolveWikiTarget(
  target: string,
  paths: readonly string[],
): string | null {
  const exact = target.split("/").filter(Boolean).join("/");
  if (exact === "") return null;
  const withMd = exact.endsWith(".md") ? null : `${exact}.md`;
  if (paths.includes(exact)) return exact;
  if (withMd !== null && paths.includes(withMd)) return withMd;
  const lower = exact.toLowerCase();
  const lowerMd = withMd?.toLowerCase();
  return (
    paths.find((p) => {
      const l = p.toLowerCase();
      return l === lower || l === lowerMd;
    }) ?? null
  );
}
